import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Loader2, ShieldCheck, Crown } from "lucide-react";
import { motion } from "framer-motion";

const LOGIN_BG =
  "https://images.unsplash.com/photo-1759185301790-e079e2970809?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAxODF8MHwxfHNlYXJjaHw0fHxtaW5pbWFsaXN0JTIwbHV4dXJ5JTIwc2lsdmVyJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODgzMTY2ODJ8MA&ixlib=rb-4.1.0&q=85";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (!res.ok) setError(res.error);
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      {/* Left brand panel */}
      <div className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden bg-[#1A1024]">
        <img
          src={LOGIN_BG}
          alt=""
          className="absolute inset-0 h-full w-full object-cover opacity-25"
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#2D1B3D]/80 via-[#1A1024]/90 to-black/90" />
        <div className="relative z-10 flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-[#581C87] flex items-center justify-center">
            <Crown className="h-6 w-6 text-white" />
          </div>
          <span className="text-white font-heading font-bold text-lg tracking-tight">
            KISSED Ecosystem OS
          </span>
        </div>
        <div className="relative z-10 max-w-md">
          <p className="text-xs uppercase tracking-widest text-purple-300 mb-4">
            Executive Command Center
          </p>
          <h1 className="font-heading text-4xl font-bold text-white tracking-tight leading-tight">
            One control plane for the entire ecosystem.
          </h1>
          <p className="mt-5 text-gray-300 leading-relaxed">
            Coordinate every business operating system from a single, secure
            command center built for Dr. Christy Vonta Bell.
          </p>
        </div>
        <div className="relative z-10 flex items-center gap-2 text-gray-400 text-sm">
          <ShieldCheck className="h-4 w-4 text-emerald-400" />
          Secure Super Admin access only
        </div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-6 sm:p-12 bg-[#F9FAFB]">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-md"
        >
          <div className="lg:hidden flex items-center gap-3 mb-8">
            <div className="h-10 w-10 rounded-xl bg-[#581C87] flex items-center justify-center">
              <Crown className="h-5 w-5 text-white" />
            </div>
            <span className="font-heading font-bold text-lg">KISSED Ecosystem OS</span>
          </div>

          <p className="text-xs uppercase tracking-widest text-[#581C87] font-semibold mb-2">
            Super Admin Login
          </p>
          <h2 className="font-heading text-3xl font-bold tracking-tight text-gray-900">
            Welcome back
          </h2>
          <p className="text-gray-500 mt-2 mb-8">
            Sign in to access your ecosystem command center.
          </p>

          <form onSubmit={onSubmit} className="space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="focus-visible:ring-[#581C87]"
                data-testid="login-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <Input
                id="password"
                type="password"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="focus-visible:ring-[#581C87]"
                data-testid="login-password-input"
              />
            </div>

            {error && (
              <p className="text-sm text-red-600" data-testid="login-error">
                {error}
              </p>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-[#581C87] hover:bg-[#6B21A8] text-white h-11 rounded-md"
              data-testid="login-submit-button"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign In"}
            </Button>
          </form>
        </motion.div>
      </div>
    </div>
  );
}
